package com.neurobs.ccabserver.administrator.controller

import com.neurobs.ccab.{SessionSearchData, StudyTypeUpdateData, IdentityData}
import com.neurobs.ccab.db.StudyTypeData
import com.neurobs.js.ccab.CCABServer
import com.neurobs.js.controller.{AdminListAdapter, AdminListController, AdminPanelController, AdminPanelDelegate}
import com.neurobs.js.dom.ElementFactory._
import com.neurobs.js.dom.DOMViewElement._
import com.neurobs.js.dom.RecordTableView.CommandLinkCell
import com.neurobs.js.view.{Form, Gui}
import com.neurobs.js.util.StringUtilities.dateString
import com.neurobs.js.dom.{InputTextBox, TextAreaTextBox}
import scalatags.JsDom.all._

import scala.concurrent.{Future, Promise}

class StudyTypeListController(gui: Gui, userData: IdentityData, addPanel: AdminPanelController => Unit)(implicit server: com.neurobs.ccab.CCABServer) extends AdminListAdapter[StudyTypeData] {

	override def title = "Study Types"

	override def itemName = "Study Type"

	override def columnHeaders = Seq(
		"DID",
		"Created",
		"Name",
		"Description",
		"",
		"",
		""
	)

	override def itemRow(t: StudyTypeData, delegate: Option[AdminPanelDelegate], controller: AdminListController[StudyTypeData]) = Seq(
		t.id.toString,
		dateString(t.createdAt),
		t.displayName,
		t.description,
		CommandLinkCell("edit", () => createOrEdit(Some(t), controller)),
		CommandLinkCell("delete", () => delete(t, controller)),
		CommandLinkCell(text = "subjects", () => delegate.foreach(_.newPanel(new SessionSearchController(SessionSearchData(studyTypeId = Some(t.id)), title = s"Subjects for study type '${t.displayName}' (${t.id})'", gui, userData, addPanel))))
	)

	override val create = Some(createOrEdit(None, _))

	override def get(): Future[Either[String, Seq[StudyTypeData]]] = {
		val promise = Promise[Either[String, Seq[StudyTypeData]]]
		CCABServer.getStudyTypes {
			case Left(error) => promise.success(Left(error.message))
			case Right(data) => promise.success(Right(data))
		}
		promise.future
	}

	private def delete(t: StudyTypeData, c: AdminListController[StudyTypeData]): Unit = {
		val form = makeForm(
			width = 30,
			title = "Delete",
			description = s"Delete Study Type ${t.displayName}?",
			elements = Array(),
			useCancel = true,
			submitCaption = "Delete"
		)

		def process(callback: (Option[String]) => Unit): Either[String,Form.Process] = Right(
			CCABServer.deleteStudyType(t.id) {
				case Left(err) => callback(Some(err.message))
				case Right(res) => res.error match {
					case Some(err) => callback(Some(err))
					case None =>
						callback(None)
						c.getList()
				}
			}
		)

		gui.showModalForm(form, process, processCancellable = true, Some(() => Unit))
	}

	private def createOrEdit(t: Option[StudyTypeData], c: AdminListController[StudyTypeData]): Unit = {
		val boxWidth = 20
		val formWidth = 60

		val descriptionBox: TextAreaTextBox = textarea(style := s"width:${boxWidth}em;height:5em;").render
		descriptionBox.setValue(t.map(_.description).getOrElse(""))
		val displayNameBox: InputTextBox = makeTextInput(boxWidth, t.map(_.displayName).getOrElse(""))

		val form = makeForm(
			width = formWidth,
			title = t match {
				case Some(studyType) => s"Update ${studyType.displayName}"
				case None => "Create New Study Type"
			},
			description = "",
			elements = Array(
				("Name:", displayNameBox.text),
				("Description:", descriptionBox.area)
			),
			useCancel = true,
			submitCaption = if (t.isDefined) "Update" else "Create"
		)

		def process(callback: (Option[String]) => Unit): Either[String,Form.Process] = {
			if (displayNameBox.value().isEmpty)
				Left("Name is required.")
			else {
				val data = StudyTypeUpdateData(displayName = displayNameBox.value(), description = descriptionBox.value())
				Right(
					t match {
						case Some(test) =>
							CCABServer.updateStudyType(test.id, data) {
								case Left(err) => callback(Some(err.message))
								case Right(res) => res.error match {
									case Some(err) => callback(Some(err))
									case None =>
										callback(None)
										c.getList()
								}
							}
						case None =>
							CCABServer.createStudyType(data) {
								case Left(err) => callback(Some(err.message))
								case Right(res) => res.error match {
									case Some(err) => callback(Some(err))
									case None =>
										callback(None)
										c.getList()
								}
							}
					}
				)
			}
		}

		gui.showModalForm(form, process, processCancellable = true, Some(() => Unit))
	}
}

object StudyTypeListController {
	def apply(gui: Gui, userData: IdentityData, addPanel: AdminPanelController => Unit)(implicit server: com.neurobs.ccab.CCABServer): AdminListController[StudyTypeData] = new AdminListController[StudyTypeData](new StudyTypeListController(gui, userData, addPanel))
}
